import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import OrderDesign from './OrderDesign';
import OrderSidebarOrderList from './components/OrderSidebarOrderList';
import './components/OrderSidebarStyleCard.css';
import { useTranslation } from 'react-i18next';
import { playSoundEffect } from './hooks/useSoundEffects';
import CustomDropdown from './CustomDropdown';
import MenuPaintedButton from './components/MenuPaintedButton';
import { BATTLE_UI_END_TURN_BUTTON_PATH } from './config/welcomeAssets';
import './StrategyModal.css';
import { getStrategyHudTotal } from './utils/strategyHudTotal';
import { getStrategyModalPanelStyle } from './utils/strategyModalPanelStyle';

// Helper function to get rarity sort order
const getRarityOrder = (rarity) => {
  const rarityOrder = { 'common': 1, 'rare': 2, 'epic': 3, 'legendary': 4 };
  return rarityOrder[(rarity || 'common').toLowerCase()] || 0;
};

const MAX_HAND_SIZE = 10;

function getOrderIdNum(order) {
  const idNum = Number(order?.id ?? order?.order_id);
  return Number.isFinite(idNum) ? idNum : null;
}

/** Count physical copies per order id in a pile (hand, deck, battlefield, etc.). */
function countOrdersById(orders) {
  const counts = new Map();
  for (const order of orders || []) {
    const idNum = getOrderIdNum(order);
    if (idNum == null) continue;
    counts.set(idNum, (counts.get(idNum) || 0) + 1);
  }
  return counts;
}

/** Play cost for a physical or template order (honors instanceCost when modified). */
function getEffectiveOrderCost(order) {
  if (!order) return 0;
  if (order.hasInstanceModifications && order.instanceCost !== undefined) {
    return Number(order.instanceCost);
  }
  return Number(order.cost ?? 0);
}

/** True when the player has enough intel to play this order (matches hand afford checks in BattleUI). */
function canAffordOrderWithCurrentIntel(order, playerState) {
  if (!playerState || !order) return false;
  const intelNum = Number(playerState.intelligence);
  const costNum = getEffectiveOrderCost(order);
  if (!Number.isFinite(intelNum) || !Number.isFinite(costNum)) return false;
  return intelNum >= costNum;
}

/** Pool physical pile copies by order id for merging into strategy template rows. */
function poolOrdersById(orders) {
  const pools = new Map();
  for (const order of orders || []) {
    const idNum = getOrderIdNum(order);
    if (idNum == null) continue;
    if (!pools.has(idNum)) pools.set(idNum, []);
    pools.get(idNum).push(order);
  }
  return pools;
}

function takePooledOrderCopy(pools, idNum, template) {
  const pool = pools.get(idNum);
  if (!pool || pool.length === 0) {
    return template;
  }
  return { ...template, ...pool.shift() };
}

function StrategyModal({
  show,
  onClose,
  strategy,
  gameState,
  userId = null,
  hoveredOrderIndex,
  onHoverOrder,
  playerState,
  commandDirectiveActive = false,
  onCommandDirectiveSelect = null,
  isMobile = false,
}) {
  const { t } = useTranslation();
  const [hoveredOrderGroupKey, setHoveredOrderGroupKey] = useState(null);
  const [sortBy, setSortBy] = useState('cost'); // 'cost', 'rarity', 'name', 'type'

  if (!show || !strategy?.orders) return null;

  const handSize = Array.isArray(playerState?.hand) ? playerState.hand.length : 0;
  const isHandFull = handSize >= MAX_HAND_SIZE;

  // Expand orders based on count and track which ones are drawn
  const expandedOrders = [];
  const drawnCounts = new Map(); // Copies outside the deck (hand, board, discard, completed)

  if (playerState) {
    const allDrawnOrders = [
      ...(playerState.hand || []),
      ...(playerState.discard || []),
      ...(playerState.playedOrders?.front || []),
      ...(playerState.playedOrders?.flank || []),
      ...(playerState.completedOrders || []),
    ];
    for (const [idNum, n] of countOrdersById(allDrawnOrders)) {
      drawnCounts.set(idNum, n);
    }
  }

  const strategyPileOrders = [
    ...(playerState?.deck || []),
    ...(playerState?.mulliganedOrders || []),
  ];
  const deckCounts = countOrdersById(strategyPileOrders);
  const deckOrdersById = poolOrdersById(strategyPileOrders);
  const drawnOrdersById = poolOrdersById(
    playerState
      ? [
          ...(playerState.hand || []),
          ...(playerState.discard || []),
          ...(playerState.playedOrders?.front || []),
          ...(playerState.playedOrders?.flank || []),
          ...(playerState.completedOrders || []),
        ]
      : []
  );

  strategy.orders.forEach((order) => {
    const idNum = getOrderIdNum(order);
    if (idNum == null) return;

    const strategyCount = order.count || 1;
    const drawnCount = drawnCounts.get(idNum) || 0;
    let deckSlotsRemaining = deckCounts.get(idNum) || 0;
    const physicalCount = drawnCount + deckSlotsRemaining;
    const displayCount = Math.min(strategyCount, physicalCount);

    for (let i = 0; i < displayCount; i++) {
      const isDrawn = i < drawnCount;
      const canCommandDirectiveGet =
        commandDirectiveActive && !isDrawn && deckSlotsRemaining > 0;
      if (canCommandDirectiveGet) {
        deckSlotsRemaining -= 1;
      }
      const physicalOrder = isDrawn
        ? takePooledOrderCopy(drawnOrdersById, idNum, order)
        : takePooledOrderCopy(deckOrdersById, idNum, order);
      expandedOrders.push({
        ...physicalOrder,
        isDrawn,
        canCommandDirectiveGet,
      });
    }
  });

  // Visual-only: include dynamically added units (e.g. Ter'fa-added Shadow Snipers)
  // that are present in current player piles but not in the saved base strategy list.
  const baseStrategyOrderIds = new Set((strategy.orders || []).map((o) => Number(o?.id)));
  const deckLikeOrders = [
    ...(playerState?.deck || []),
    ...(playerState?.mulliganedOrders || []),
    ...(playerState?.drawPile || []),
    ...(playerState?.draw_pile || []),
  ];
  const allVisibleOrders = [...deckLikeOrders, ...(playerState?.hand || []), ...(playerState?.discard || []), ...(playerState?.playedOrders?.front || []), ...(playerState?.playedOrders?.flank || []), ...(playerState?.completedOrders || [])];
  const extraOrderTotalCounts = new Map();
  const extraOrderDrawnCounts = new Map();
  const extraOrderRepresentatives = new Map();

  const markExtraOrder = (order, isDrawn) => {
    const idNum = Number(order?.id);
    if (!Number.isFinite(idNum) || baseStrategyOrderIds.has(idNum)) return;
    extraOrderTotalCounts.set(idNum, (extraOrderTotalCounts.get(idNum) || 0) + 1);
    if (isDrawn) {
      extraOrderDrawnCounts.set(idNum, (extraOrderDrawnCounts.get(idNum) || 0) + 1);
    }
    if (!extraOrderRepresentatives.has(idNum)) {
      extraOrderRepresentatives.set(idNum, order);
    }
  };

  deckLikeOrders.forEach((order) => markExtraOrder(order, false));
  allVisibleOrders.forEach((order) => markExtraOrder(order, true));

  for (const [orderId, totalCount] of extraOrderTotalCounts.entries()) {
    const drawnCount = extraOrderDrawnCounts.get(orderId) || 0;
    const representative = extraOrderRepresentatives.get(orderId);
    if (!representative) continue;
    for (let i = 0; i < totalCount; i++) {
      expandedOrders.push({
        ...representative,
        isDrawn: i < drawnCount,
        isDynamicAdded: true,
      });
    }
  }
  
  // Sort by selected option
  expandedOrders.sort((a, b) => {
    // Primary sort by selected option
    let primaryComparison = 0;
    switch (sortBy) {
      case 'cost':
        primaryComparison = getEffectiveOrderCost(a) - getEffectiveOrderCost(b);
        break;
      case 'rarity':
        primaryComparison = getRarityOrder(a.rarity) - getRarityOrder(b.rarity);
        break;
      case 'name':
        primaryComparison = (a.name || '').localeCompare(b.name || '');
        break;
      case 'type':
        primaryComparison = (a.type || '').localeCompare(b.type || '');
        break;
      default:
        primaryComparison = getEffectiveOrderCost(a) - getEffectiveOrderCost(b);
    }

    if (primaryComparison !== 0) return primaryComparison;

    // Secondary sort by name (except when already sorting by name)
    if (sortBy !== 'name') {
      return (a.name || '').localeCompare(b.name || '');
    }

    // If sorting by name and names are equal, sort by cost
    return getEffectiveOrderCost(a) - getEffectiveOrderCost(b);
  });
  
  const hoveredOrder = hoveredOrderIndex !== null ? expandedOrders[hoveredOrderIndex] : null;

  const modalTree = (
    <div
      className={`strategy-modal-overlay${isMobile ? ' strategy-modal-overlay--mobile' : ''}`}
      style={getStrategyModalPanelStyle(isMobile)}
      data-no-battle-flashlight="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      {/* Desktop: hover preview beside modal — off-screen on mobile */}
      {hoveredOrder && !isMobile && (
        <div
          style={{
            position: 'absolute',
            left: 'calc(50% + 300px)',
            top: '50%',
            transform: 'translateY(-50%)',
            marginLeft: '20px',
            zIndex: 15101,
            pointerEvents: 'none',
          }}
        >
          <OrderDesign
            order={hoveredOrder}
            count={0}
            minimal={false}
            lazyLoad={false}
            gameState={gameState}
            userId={userId}
          />
        </div>
      )}
      <div
        className={`strategy-modal-panel${isMobile ? ' strategy-modal-panel--mobile' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="strategy-modal-panel__safe-area">
        <div className="strategy-modal-header">
          <h3 style={{ color: 'white', margin: 0 }}>
            {t('battleUI.strategy') || 'Strategy'}
          </h3>
          <MenuPaintedButton
            type="button"
            className="strategy-modal-close-btn"
            imagePath={BATTLE_UI_END_TURN_BUTTON_PATH}
            onClick={onClose}
          >
            {t('battleUI.close') || 'Close'}
          </MenuPaintedButton>
        </div>

        <div className="strategy-modal-sort-row">
          <div style={{ color: '#aaa', fontSize: isMobile ? '0.8em' : '0.9em' }}>
            {t('battleUI.totalStrategyOrders') || 'Total'}: {getStrategyHudTotal(playerState)}
          </div>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '5px',
              color: '#aaa',
              fontSize: isMobile ? '0.8em' : '0.9em',
              whiteSpace: isMobile ? 'normal' : 'nowrap',
            }}
          >
            {t('strategy.sortBy') || 'Sort by:'}
            <div
              className="strategy-modal-sort-dropdown"
              style={{ minWidth: isMobile ? undefined : '150px', maxWidth: isMobile ? '100%' : '150px' }}
            >
              <CustomDropdown
                options={[
                  { value: 'cost', label: t('strategy.sort.cost') || 'Cost' },
                  { value: 'rarity', label: t('strategy.sort.rarity') || 'Rarity' },
                  { value: 'name', label: t('strategy.sort.name') || 'Name' },
                  { value: 'type', label: t('strategy.sort.type') || 'Type' },
                ]}
                value={sortBy}
                onChange={(value) => {
                  playSoundEffect('/sounds/click.mp3');
                  setSortBy(value);
                }}
                placeholder={t('strategy.sortBy') || 'Sort by:'}
              />
            </div>
          </label>
        </div>

        <div className="strategy-modal-list">
          <OrderSidebarOrderList
            items={expandedOrders}
            getGroupKey={(order) => `${order.id}|${order.isDrawn ? 'drawn' : 'active'}`}
            getDisplayName={(order) => order.name || 'Unknown Order'}
            getDisplayCost={(order) => {
              const cost = getEffectiveOrderCost(order);
              return Number.isFinite(cost) ? cost : 'N/A';
            }}
            costLabel={t('strategy.cost')}
            renderRowWrapperClassName={(order, group) => {
              const isDrawn = order.isDrawn || false;
              const groupKey = group.key;
              const isHovered = hoveredOrderGroupKey === groupKey;
              const showGetButton =
                commandDirectiveActive &&
                !isDrawn &&
                order.canCommandDirectiveGet &&
                !order.isDynamicAdded &&
                onCommandDirectiveSelect &&
                (isMobile || isHovered);
              return `${isDrawn ? 'strategy-modal-order-entry--drawn' : ''}${
                showGetButton ? ' strategy-modal-order-entry--with-actions' : ''
              }`.trim();
            }}
            onItemMouseEnter={(order, group) => {
              if (!isMobile) {
                playSoundEffect('/sounds/hoverover.mp3', 0.3);
              }
              onHoverOrder(group.firstIndex);
              setHoveredOrderGroupKey(group.key);
            }}
            onItemMouseLeave={() => {
              onHoverOrder(null);
              setHoveredOrderGroupKey(null);
            }}
            renderAfterCard={(order, group) => {
              const isDrawn = order.isDrawn || false;
              const isHovered = hoveredOrderGroupKey === group.key;
              const canAffordGet = canAffordOrderWithCurrentIntel(order, playerState);
              const showGetButton =
                commandDirectiveActive &&
                !isDrawn &&
                order.canCommandDirectiveGet &&
                !order.isDynamicAdded &&
                onCommandDirectiveSelect &&
                (isMobile || isHovered);
              if (!showGetButton) return null;
              return (
                <button
                  type="button"
                  className={`strategy-modal-get-btn${canAffordGet && !isHandFull ? ' strategy-modal-get-btn--affordable' : ''}`}
                  disabled={isHandFull}
                  title={isHandFull ? (t('battleUI.handFullCannotGet') || 'Hand is full (10/10)') : undefined}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isHandFull) return;
                    onCommandDirectiveSelect(order.id);
                  }}
                >
                  {t('battleUI.get') || 'Get'}
                </button>
              );
            }}
          />
        </div>
        </div>
      </div>
    </div>
  );

  // Mobile: stay inside the scaled viewport-container (canvas coords), not document.body.
  if (isMobile) {
    return modalTree;
  }
  if (typeof document === 'undefined' || !document.body) {
    return modalTree;
  }
  return createPortal(modalTree, document.body);
}

export default StrategyModal;

